import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

import { AccountStatus, OnboardingStep } from 'services/dashboard/type';
import { UserAttributes } from '../types';
import { MemberDetail } from 'services/profile/type';

export interface AuthState {
  isAuthenticated: boolean;
  tokenGeneratedTime: string;
  tokenExpiryTime: string;
  signupEmail: string;
  signupPhone: string;
  signupPassword: string;
  signupUserId: string;
  forgotEmail: string;
  locale: 'es' | 'en';
  accountStatus: AccountStatus | null;
  onboardingSteps: OnboardingStep[];
  onboarding: OnboardingStep[];
  currentMilestone: string;
  userAttributes: UserAttributes | null;
  userEmail: string;
  memberDetails: MemberDetail[];
  adminDetails: UserAttributes | null;
  currentUserProfileId: string;
  isCurrentUserAdmin: boolean;
  adminProfileId: string;
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
}

interface AuthAction {
  setIsAuthenticated: (authenticated: boolean) => void;
  setTokenGeneratedTime: (time: string) => void;
  setTokenExpiryTime: (time: string) => void;
  setSignupEmail: (email: string) => void;
  setSignupPhone: (phone: string) => void;
  setSignupPassword: (password: string) => void;
  setSignupUserId: (password: string) => void;
  setForgotEmail: (email: string) => void;
  setLocale: (locale: 'es' | 'en') => void;
  setAccountStatus: (accountStatus: AccountStatus | null) => void;
  setOnBoardingSteps: (steps: OnboardingStep[]) => void;
  setOnBoarding: (onboarding: OnboardingStep[]) => void;
  setCurrentMilestone: (milestone: string) => void;
  setUserAttributes: (userAttributes: UserAttributes) => void;
  setUserEmail: (userEmail: string) => void;
  setMemberDetails: (memberDetails: MemberDetail[]) => void;
  setAdminDetails: (adminDetails: UserAttributes | null) => void;
  setCurrentUserProfileId: (id: string) => void;
  setAdminProfileId: (id: string) => void;
  setPhoneVerified: (verified: boolean) => void;
  setEmailVerified: (verified: boolean) => void;
  logout: () => void;
  resetSignUp: () => void;
}

export const authInitalState: AuthState = {
  isAuthenticated: false,
  tokenGeneratedTime: '',
  tokenExpiryTime: '',
  signupEmail: '',
  signupPhone: '',
  signupPassword: '',
  signupUserId: '',
  forgotEmail: '',
  locale: 'en',
  accountStatus: null,
  onboardingSteps: [],
  currentMilestone: '',
  userAttributes: null,
  userEmail: '',
  memberDetails: [],
  adminDetails: null,
  currentUserProfileId: '',
  isCurrentUserAdmin: false,
  adminProfileId: '',
  onboarding: [],
  isPhoneVerified: false,
  isEmailVerified: false,
};

export type AuthStore = AuthState & AuthAction;

const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...authInitalState,
        setIsAuthenticated: (isAuthenticated: boolean) =>
          set({ isAuthenticated }),
        setTokenGeneratedTime: (token: string) =>
          set({ tokenGeneratedTime: token }),
        setTokenExpiryTime: (token: string) => set({ tokenExpiryTime: token }),
        setSignupEmail: (email: string) => set({ signupEmail: email }),
        setSignupUserId: (userId: string) => set({ signupUserId: userId }),
        setSignupPhone: (phone: string) => set({ signupPhone: phone }),
        setSignupPassword: (password: string) =>
          set({ signupPassword: password }),
        setForgotEmail: (email: string) => set({ forgotEmail: email }),
        setLocale: (locale: 'es' | 'en') => set({ locale }),
        setAccountStatus: (status: AccountStatus | null) =>
          set({ accountStatus: status }),
        setOnBoardingSteps: (steps: OnboardingStep[]) =>
          set({ onboardingSteps: steps }),
        setOnBoarding: (onboarding: OnboardingStep[]) => set({ onboarding }),
        setCurrentMilestone: (milestone: string) =>
          set({ currentMilestone: milestone }),
        setUserAttributes: (userAttributes: UserAttributes) =>
          set({ userAttributes: userAttributes }),
        setUserEmail: (userEmail: string) => set({ userEmail: userEmail }),
        setMemberDetails: (memberDetails: MemberDetail[]) =>
          set({ memberDetails: memberDetails }),
        setAdminDetails: (memberDetails: UserAttributes | null) =>
          set({ adminDetails: memberDetails }),
        setCurrentUserProfileId: (id: string) =>
          set({
            currentUserProfileId: id,
            isCurrentUserAdmin:
              get().memberDetails.find((member) => member.profile_id === id)
                ?.relation === 'Admin',
          }),
        setAdminProfileId: (id: string) => set({ adminProfileId: id }),
        setPhoneVerified: (verified: boolean) =>
          set({ isPhoneVerified: verified }),
        setEmailVerified: (verified: boolean) =>
          set({ isEmailVerified: verified }),
        resetSignUp: () =>
          set({
            signupEmail: '',
            signupPassword: '',
            signupPhone: '',
            signupUserId: '',
            isPhoneVerified: false,
            isEmailVerified: false,
          }),
        logout: () => set({ ...authInitalState, locale: get().locale }),
      }),
      { name: 'auth' }
    )
  )
);

export default useAuthStore;
